import React from 'react';
import { Flex, IconButton, Text } from '@vibe/core';
import { Close, Delete, Edit } from '@vibe/icons';
import { format, parseISO } from 'date-fns';
import type { WorklogRow } from './TimeTrackTable';
import { useUsersStore } from '../../stores/usersStore';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface WorklogListPopupProps {
  /** The worklogs to show in this popup */
  worklogs: WorklogRow[];
  /** The date this cell represents */
  date: string;
  /** The user name for the header */
  userName: string;
  /** Callback to edit a worklog */
  onEdit: (id: string) => void;
  /** Callback to delete a worklog */
  onDelete: (id: string) => void;
  /** Callback to close the popup */
  onClose: () => void;
  /** When true, hides edit/delete controls (period is locked) */
  readOnly?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * A popup that displays the list of worklogs for a specific user × day cell
 * in the matrix view. Shows item name, hours, and description for each entry
 * with edit/delete actions.
 */
const WorklogListPopup: React.FC<WorklogListPopupProps> = ({
  worklogs,
  date,
  userName,
  onEdit,
  onDelete,
  onClose,
  readOnly = false,
}) => {
  const users = useUsersStore((s) => s.users);
  const totalHours = worklogs.reduce(
    (sum, w) => sum + (Number(w.hours) || 0),
    0,
  );

  const formattedDate = format(parseISO(date), 'EEEE, MMMM d, yyyy');

  return (
    <div className="rp-wl-popup-overlay" onClick={onClose}>
      <div
        className="rp-wl-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rp-wl-popup__header">
          <div className="rp-wl-popup__header-info">
            <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
              {userName}
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {formattedDate}
            </Text>
          </div>
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
            <span className="rp-wl-popup__total-badge">
              {worklogs.length}{' '}
              {worklogs.length === 1 ? 'worklog' : 'worklogs'} |{' '}
              {formatHoursMinutes(totalHours)}
            </span>
            <IconButton
              icon={Close}
              size={IconButton.sizes.SMALL}
              onClick={onClose}
              ariaLabel="Close"
            />
          </Flex>
        </div>

        {/* Worklog list */}
        <div className="rp-wl-popup__list">
          {worklogs.map((w) => (
            <div key={w.id} className="rp-wl-popup__item">
              <div className="rp-wl-popup__item-body">
                <Text
                  type={Text.types.TEXT2}
                  weight={Text.weights.MEDIUM}
                  ellipsis
                >
                  {w.item_name ?? `Item #${w.monday_item_id}`}
                </Text>
                {(w.board_name || w.group_name) && (
                  <Text
                    type={Text.types.TEXT2}
                    ellipsis
                    style={{ color: '#676879', fontSize: '11px', marginTop: '1px' }}
                  >
                    {[w.board_name, w.group_name].filter(Boolean).join(' > ')}
                  </Text>
                )}
                <Text
                  type={Text.types.TEXT1}
                  weight={Text.weights.BOLD}
                  style={{ color: '#0073ea', marginTop: '2px' }}
                >
                  {formatHoursMinutes(w.hours)}
                </Text>
                {w.description && (
                  <Text
                    type={Text.types.TEXT2}
                    ellipsis
                    style={{ color: '#323338', marginTop: '2px' }}
                    tooltipProps={{ content: w.description }}
                  >
                    {w.description}
                  </Text>
                )}
                {w.createdByUserId && w.createdByUserId !== w.monday_user_id && (
                  <Text
                    type={Text.types.TEXT2}
                    style={{ color: '#676879', fontSize: '11px', marginTop: '2px' }}
                  >
                    Created by: {users.find((u) => String(u.id) === w.createdByUserId)?.name ?? `User #${w.createdByUserId}`}
                  </Text>
                )}
              </div>
              {!readOnly && (
                <div className="rp-wl-popup__item-actions">
                  <IconButton
                    icon={Edit}
                    size={IconButton.sizes.XXS}
                    onClick={() => onEdit(w.id)}
                    ariaLabel="Edit worklog"
                  />
                  <IconButton
                    icon={Delete}
                    size={IconButton.sizes.XXS}
                    onClick={() => onDelete(w.id)}
                    ariaLabel="Delete worklog"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorklogListPopup;
